<script setup lang="ts">
import { EmbeddedDropdown, useInfiniteScroll } from '#shared'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, useTemplateRef } from 'vue'
import { alertsQueryOptions } from '../state/alerts-query'
import AlertsDropdownEmptyState from './alerts-dropdown-empty-state.vue'
import AlertsDropdownItem from './alerts-dropdown-item.vue'

const { toggleEl } = defineProps<{ toggleEl: HTMLElement }>()

const emit = defineEmits<{ close: [] }>()

const alertsQuery = useInfiniteQuery(alertsQueryOptions)
const alerts = computed(() => alertsQuery.data.value?.pages.flatMap(page => page.data) ?? [])

const listEl = useTemplateRef('listEl')
useInfiniteScroll(listEl, {
  direction: 'down',
  canLoadDown: computed(() => alertsQuery.hasNextPage.value && !alertsQuery.isFetching.value),
  loadDown: () => alertsQuery.fetchNextPage(),
})

const onAlertLinkClick = () => emit('close')
</script>

<template>
  <EmbeddedDropdown :toggleEl="toggleEl">
    <template #title>Alerts</template>

    <template #content>
      <ul v-if="alerts.length > 0" :class="$style.list" ref="listEl">
        <AlertsDropdownItem v-for="alert in alerts" :key="alert.id" :alert="alert" @link-click="onAlertLinkClick" />
      </ul>

      <AlertsDropdownEmptyState v-else-if="alertsQuery.isPending.value" label="Loading..." />

      <AlertsDropdownEmptyState v-else />
    </template>
  </EmbeddedDropdown>
</template>

<style lang="scss" module>
@use '#mixins/scrollbar' as scrollbar;

.list {
  min-height: 200px;
  max-height: 470px;
  overflow: auto;
  list-style: none;
  padding: 0;
  margin: 0;

  @include scrollbar.thick(#1f1f22);
}
</style>
